import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { Avatar } from "@/components/AppHeader";
import api, { formatApiError } from "@/lib/api";
import {
  Loader2, MapPin, Edit3, ChevronLeft, Settings as SettingsIcon,
  AlertTriangle, MessageSquare, PlusSquare, Inbox, Plus,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { t } from "@/lib/strings";
import { toast } from "sonner";

export default function ProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const { language } = useI18n();
  const [profile, setProfile] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const isMe = me?.id === id;

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/users/${id}`);
        setProfile(data);
        const { data: allReports } = await api.get(`/reports`);
        setReports(allReports.filter((r) => r.author?.id === id));
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  const stats = useMemo(() => {
    const total = reports.length;
    const resolved = reports.filter((r) => r.status === "resolved").length;
    const support = reports.reduce((s, r) => s + (r.likes_count || 0), 0);
    return { total, resolved, support };
  }, [reports]);

  const cityName = useMemo(() => {
    if (!profile) return "";
    if (language === "ar") return profile.city_ar || profile.city_en || profile.city_he || "";
    if (language === "he") return profile.city_he || profile.city_en || profile.city_ar || "";
    return profile.city_en || profile.city_he || profile.city_ar || "";
  }, [profile, language]);

  const topBar = (
    <div className="h-14 px-4 flex items-center gap-2">
      <button
        onClick={() => navigate(-1)}
        data-testid="profile-back-btn"
        className="w-9 h-9 rounded-full bg-[var(--te-input-bg)] hover:bg-[#E5E7F3] flex items-center justify-center"
      >
        <ChevronLeft size={20} />
      </button>
      <h1 className="font-extrabold text-[20px] text-[var(--te-text)] flex-1 truncate" dir="auto">
        {isMe ? (t("tab_account", language)) : (profile?.name || "—")}
      </h1>
    </div>
  );

  if (loading) {
    return (
      <MobileShell topBar={topBar}>
        <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-[var(--te-primary)]" /></div>
      </MobileShell>
    );
  }
  if (!profile) {
    return (
      <MobileShell topBar={topBar}>
        <div className="text-center py-24 text-[var(--te-text-secondary)]">User not found.</div>
      </MobileShell>
    );
  }

  return (
    <MobileShell topBar={topBar}>
      <div className="px-4 pt-4 pb-6 space-y-4">
        {/* Identity row */}
        <div className="flex flex-col items-center gap-3 te-fade-up" data-testid="profile-identity">
          <Avatar user={profile} size={86} />
          <h2 className="text-[22px] font-extrabold text-[var(--te-text)]" dir="auto">{profile.name}</h2>
          <div className="flex items-center gap-2 flex-wrap justify-center">
            {cityName && (
              <span className="te-pill text-[12px]" data-testid="profile-city-pill">
                <MapPin size={12} /> {cityName}
              </span>
            )}
            <span className="te-pill text-[12px] !bg-[#CFFAFE] !text-[#0E7490]">
              {profile.role === "admin" ? "Admin" : t("citizen", language)}
            </span>
          </div>
          {profile.bio ? (
            <p className="text-center text-[var(--te-text-secondary)] text-sm leading-relaxed max-w-xs" dir="auto">
              {profile.bio}
            </p>
          ) : isMe ? (
            <Link
              to="/profile/edit"
              data-testid="profile-bio-prompt"
              className="text-[var(--te-primary)] text-[13px] font-semibold inline-flex items-center gap-1.5"
            >
              <Edit3 size={13} /> {t("profile_add_bio", language)}
            </Link>
          ) : null}

          {isMe && (
            <Link
              to="/profile/edit"
              data-testid="profile-edit-btn"
              className="te-btn-secondary text-[13px] mt-1 inline-flex items-center gap-1.5"
            >
              <Edit3 size={13} /> {t("profile_edit_button", language)}
            </Link>
          )}
        </div>

        {/* Stats card */}
        <div className="te-card p-5 te-fade-up" data-testid="profile-stats">
          <div className="grid grid-cols-3 divide-x divide-[var(--te-divider)] rtl:divide-x-reverse">
            <Stat value={stats.total} label={t("profile_stat_reports", language)} color="#1C211F" testId="stat-reports" />
            <Stat value={stats.resolved} label={t("profile_stat_resolved", language)} color="#10B981" testId="stat-resolved" />
            <Stat value={stats.support} label={t("profile_stat_support", language)} color="#06B6D4" testId="stat-support" />
          </div>
        </div>

        {/* Actions row — 4 tiles in one row, same as the mobile app */}
        {isMe && (
          <div className="grid grid-cols-4 gap-2.5" data-testid="profile-actions-grid">
            <ActionCard
              to="/report/new"
              testId="profile-action-new"
              icon={PlusSquare}
              label={t("profile_action_new", language)}
              variant="primary"
            />
            <ActionCard
              to="/reports?author=me"
              testId="profile-action-mine"
              icon={AlertTriangle}
              label={t("profile_action_my", language)}
              variant="danger"
            />
            <ActionCard
              to="/messages"
              testId="profile-action-messages"
              icon={MessageSquare}
              label={t("profile_action_messages", language)}
              variant="info"
            />
            <ActionCard
              to="/settings"
              testId="profile-action-settings"
              icon={SettingsIcon}
              label={t("profile_action_settings", language)}
              variant="neutral"
            />
          </div>
        )}

        {/* Latest reports */}
        <div data-testid="profile-latest-reports">
          <h3 className="text-[13px] font-semibold text-[var(--te-text-secondary)] mb-2 px-1">
            {isMe ? t("profile_latest_reports", language) : (language === "ar" ? "البلاغات" : language === "he" ? "דיווחים" : "Reports")}
          </h3>
          {reports.length === 0 ? (
            <div className="te-card p-8 text-center te-fade-up">
              <div className="w-14 h-14 mx-auto rounded-2xl bg-[var(--te-primary-soft)] flex items-center justify-center mb-3">
                <Inbox size={22} className="text-[var(--te-primary)]" />
              </div>
              <h4 className="text-[16px] font-bold text-[var(--te-text)]">{t("profile_empty_title", language)}</h4>
              <p className="text-[13px] text-[var(--te-text-secondary)] mt-2 max-w-[260px] mx-auto leading-relaxed">
                {t("profile_empty_sub", language)}
              </p>
              {isMe && (
                <Link
                  to="/report/new"
                  data-testid="profile-empty-cta"
                  className="te-btn-primary text-[14px] mt-4 inline-flex"
                >
                  <Plus size={14} /> {t("profile_action_new", language)}
                </Link>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {reports.slice(0, 6).map((r) => (
                <Link
                  key={r.id}
                  to={`/reports/${r.id}`}
                  data-testid={`profile-report-${r.id}`}
                  className="block te-card te-card-hover p-4 te-fade-up"
                >
                  <h4 className="text-[15px] font-bold text-[var(--te-text)] truncate" dir="auto">{r.title}</h4>
                  <p className="text-[12px] text-[var(--te-text-muted)] mt-1 line-clamp-2" dir="auto">{r.description}</p>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </MobileShell>
  );
}

function Stat({ value, label, color, testId }) {
  return (
    <div className="flex flex-col items-center text-center px-2" data-testid={testId}>
      <span className="text-[26px] font-extrabold leading-none" style={{ color }}>{value}</span>
      <span className="text-[12px] text-[var(--te-text-secondary)] mt-1">{label}</span>
    </div>
  );
}

function ActionCard({ to, icon: Icon, label, variant, testId }) {
  const styles = {
    primary: { bg: "bg-[var(--te-primary)]", text: "text-white", iconBg: "bg-white/15", iconColor: "text-white", labelText: "text-white" },
    danger:  { bg: "bg-white border border-[var(--te-divider)]", text: "text-[var(--te-text)]", iconBg: "bg-[#FEE2E2]", iconColor: "text-[#DC2626]", labelText: "text-[var(--te-text)]" },
    info:    { bg: "bg-white border border-[var(--te-divider)]", text: "text-[var(--te-text)]", iconBg: "bg-[#CFFAFE]", iconColor: "text-[#06B6D4]", labelText: "text-[var(--te-text)]" },
    neutral: { bg: "bg-white border border-[var(--te-divider)]", text: "text-[var(--te-text)]", iconBg: "bg-[#F3F4F6]", iconColor: "text-[#4B5563]", labelText: "text-[var(--te-text)]" },
  }[variant];

  return (
    <Link
      to={to}
      data-testid={testId}
      className={`rounded-[16px] p-3 flex flex-col items-center justify-center gap-1.5 min-h-[88px] te-fade-up transition-transform hover:scale-[1.03] ${styles.bg}`}
    >
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${styles.iconBg}`}>
        <Icon size={18} className={styles.iconColor} strokeWidth={2.2} />
      </div>
      <span className={`text-[12px] font-bold ${styles.labelText} text-center leading-tight`}>{label}</span>
    </Link>
  );
}
